import { authManager } from '@/shared/utils/auth/auth-manager';

// --- Core: trigger browser download from an in-memory Blob

/**
 * Downloads a blob as a file by creating a temporary anchor element
 * @param blob - The blob to download
 * @param filename - The name of the file to download
 */
export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Creates an object URL from a blob for preview purposes
 * Remember to revoke the URL when done using URL.revokeObjectURL()
 * @param blob - The blob to create a URL for
 * @returns The object URL
 */
export function createBlobUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
}

/**
 * Generates a timestamped filename for a document
 * @param documentType - The type of document
 * @param extension - File extension (e.g., 'pdf', 'docx')
 * @returns Formatted filename with timestamp
 */
export function generateDocumentFilename(documentType: string, extension: string): string {
    return `${documentType}-${Date.now()}.${extension}`;
}

// --- From URL: browser downloads it directly

/**
 * Lets the browser download a URL itself, with its own progress and straight to disk.
 * For cross-origin URLs the saved name comes from the response's Content-Disposition, `filename` only applies same-origin.
 * @param url - The URL to download
 * @param filename - The name of the file to download
 */
export function downloadFromUrl(url: string, filename?: string): void {
    const link = document.createElement('a');
    link.href = url;
    if (filename) {
        link.download = filename;
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// --- From URL: fetch → blob → download (Cockpit parity for durable-job reactions)

/**
 * Download a file from a URL into the browser (Cockpit parity for durable-job success reactions).
 */
export async function downloadBlobFromUrl(url: string, fileName?: string, useAuth?: boolean): Promise<void> {
    const headers: HeadersInit = {};
    if (useAuth) {
        const token = await authManager.getToken();
        headers.Authorization = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'GET', headers });
    const disposition = response.headers.get('Content-Disposition');
    const filenameOrDisposition = fileName ?? disposition?.split('filename=')[1]?.replace(/"/g, '');
    if (!filenameOrDisposition) {
        console.error('Could not determine filename from response or fallback');
        return;
    }
    const data = await response.blob();
    downloadBlob(data, filenameOrDisposition);
}
